<template>
  <MChart
    ref="chart"
    :series="displaySeries"
    :options="chartOptions"
    :height="height"
  />
</template>

<script>
  import MChart from 'legacy/modules/common/components/MChart/MChart';
  import formatNumber from 'legacy/modules/common/components/MNumeric/formatNumber';
  import translation from 'translation';
  import pieWidgetTooltip from 'components/Dashboards/Widgets/PieWidget/pieWidgetTooltip';
  import ColorGetterMixin from 'components/Dashboards/Widgets/mixins/ColorGetterMixin';

  const PIE_MIN_ANGLE = 10;
  const PIE_SUBTRACT_LIMIT = PIE_MIN_ANGLE * 2;


  export default {
    name: 'PieWidgetChart',
    components: {MChart},
    mixins: [ColorGetterMixin],
    props: {
      statistics: {
        type: Array,
        default: Array
      },
      metric: {
        type: String,
        default: ''
      },
      grouping: {
        type: String,
        default: ''
      },
      metricType: {
        type: String,
        default: 'integer'
      },
      height: {
        type: String,
        default: '100%'
      },
      total: {
        type: Number,
        default: null
      }
    },
    computed: {
      legendPosition () {
        return this.statistics.length > 4 ? 'right' : 'bottom';
      },
      metricValue () {
        return this.statistics[this.metric];
      },
      metricSeries () {
        return this.statistics.map(i => Number(i[this.metric]));
      },
      displaySeries () {
        let smallCount = 0;
        let smallSum = 0;

        const sum = this.metricSeries.reduce((acc, val) => acc + val, 0);

        const metricsDeg = this.metricSeries.map(val => {
          const valDeg = val / sum * 360;
          if (valDeg < PIE_MIN_ANGLE) {
            smallCount += 1;
            smallSum += valDeg;
          } else if (valDeg < PIE_SUBTRACT_LIMIT) {
            smallCount += 1;
          }
          return valDeg;
        });

        let fixedMetrics = metricsDeg;

        if (smallCount > 0) {
          const subtractVal = (smallCount * PIE_MIN_ANGLE - smallSum) / (metricsDeg.length - smallCount);

          fixedMetrics = metricsDeg.map(val => {
            if (val < PIE_MIN_ANGLE) {
              return PIE_MIN_ANGLE;
            } else if (val < PIE_SUBTRACT_LIMIT) {
              return val;
            }
            return val - subtractVal;
          });
        }

        return fixedMetrics;
      },
      actualSeries () {
        return this.metricSeries;
      },
      showLegend () {
        return this.grouping !== 'no';
      },
      labels () {
        if (this.grouping === 'no') {
          return [''];
        }

        return this.statistics.map(i => i[`${this.grouping}_name`]);
      },
      chartOptions () {
        return {
          colors: this.chartColors,
          labels: this.labels,
          chart: {
            type: 'donut'
          },
          dataLabels: {
            enabled: false
          },
          legend: {
            show: this.showLegend,
            showForSingleSeries: false,
            showForNullSeries: true,
            showForZeroSeries: true,
            position: this.legendPosition,
            itemMargin: {
              horizontal: 4,
              vertical: 8
            },
            onItemClick: {
              toggleDataSeries: false
            },
            onItemHover: {
              highlightDataSeries: false
            }
          },
          plotOptions: {
            pie: {
              expandOnClick: false,
              dataLabels: {
                offset: 0,
                minAngleToShowLabel: 10
              },
              donut: {
                size: '70%',
                labels: {
                  show: true,
                  value: {
                    show: true,
                    fontSize: '18px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    color: '#333333',
                    offsetY: 0,
                    formatter: val => val
                  },
                  total: {
                    show: true,
                    showAlways: true,
                    label: translation.tr('Total'),
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    color: '#333333',
                    formatter: () => formatNumber(this.total, this.metricType, {
                      withCurrencySymbol: true,
                      shortenNumber: true
                    })
                  }
                }
              }
            }
          },
          tooltip: {
            custom: pieWidgetTooltip(this.actualSeries, this.formatter.bind(this))
          }
        };
      }
    },
    methods: {
      formatter (val) {
        return formatNumber(val, this.metricType, {withCurrencySymbol: true});
      }
    }
  };
</script>

